import React from 'react';
import styled from 'styled-components';
import { colors } from '../../../design-system/tokens/colors';
import { CheckCircleIcon, ExclamationIcon, XCircleIcon, PlayIcon } from '@heroicons/react/outline';
import type { TrackPostsCellData } from '../types';

interface TrackPostsCellProps {
  data?: TrackPostsCellData;
  loading?: boolean;
  onSearch: () => void;
  onView?: () => void;
}

// Match the styling of normal data cells
const CellContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 20px;
  font-size: 11px;
  line-height: 1.4;
  color: #6b7c93;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  color: #a3aed0;
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const StatusText = styled.span<{ $status: TrackPostsCellData['status'] }>`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 400;
  color: ${props => {
    switch (props.$status) {
      case 'verified':
        return '#059669';
      case 'pending_verification':
        return '#d97706';
      case 'rejected':
        return '#dc2626';
      case 'not_found':
        return '#6b7280';
      default:
        return '#6b7c93';
    }
  }};
  
  svg {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
  }
`;

const SkeletonLoader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 2px 0;
`;

const SkeletonLine = styled.div<{ $width?: string }>`
  height: 12px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
  border-radius: 4px;
  width: ${props => props.$width || '100%'};
  
  @keyframes skeleton-loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const getStatusIcon = (status: TrackPostsCellData['status']) => {
  switch (status) {
    case 'verified':
      return <CheckCircleIcon />;
    case 'pending_verification':
      return <ExclamationIcon />;
    case 'rejected':
      return <XCircleIcon />;
    case 'not_found':
      return <XCircleIcon />;
    default:
      return null;
  }
};

const getStatusText = (status: TrackPostsCellData['status']) => {
  switch (status) {
    case 'verified':
      return 'Verified';
    case 'pending_verification':
      return 'Pending';
    case 'rejected':
      return 'Rejected';
    case 'not_found':
      return 'Not Found';
    case 'not_searched':
    default:
      return '';
  }
};

export const TrackPostsCell: React.FC<TrackPostsCellProps> = ({
  data,
  loading = false,
  onSearch,
  onView
}) => {
  const status = data?.status || 'not_searched';
  
  // Show skeleton while loading/searching
  if (loading || status === 'searching') {
    return (
      <CellContainer>
        <SkeletonLoader>
          <SkeletonLine $width="60%" />
        </SkeletonLoader>
      </CellContainer>
    );
  }
  
  // Empty state - show play icon (non-interactive - use column execution)
  if (status === 'not_searched') {
    return (
      <CellContainer>
        <EmptyState>
          <PlayIcon />
        </EmptyState>
      </CellContainer>
    );
  }
  
  // Show status text for all other states (matching normal cell styling)
  return (
    <CellContainer>
      <StatusText $status={status}>
        {getStatusIcon(status)}
        {getStatusText(status)}
      </StatusText>
    </CellContainer>
  );
};

